import React, { useContext, useEffect, useState } from 'react';
import { compose } from 'recompose';
import {
  withRouter,
  useParams,
  Redirect,
  RouteComponentProps,
} from 'react-router-dom';
import { Box, Card, Button, Text } from 'grommet';
import { FormNext } from 'grommet-icons';

import { usePlayerPrimaryInfo } from '../Player/playerHooks';
import withLayout from '../../helpers/withLayout';
import { BasicLoader } from '../../helpers/Loaders';
import { formatDownloadTitle } from '@helpers/utils/formatUtils';
import { getPrivacyName } from '@helpers/utils/userPlayerUtils';
import { analytics } from '../../services/analytics';
import useZendeskWidget from '../../helpers/useZendeskWidget';

import { UserContext } from '../../services/Session';

import useEntitlements from '@hooks/monetization/useEntitlements';

import {
  ViewerEntitlementStatus,
  PaywallContext,
} from '../../components/Paywall/paywall.types';

import PaywallLock from '../../components/Paywall/PaywallLock';
import PaywallCta from '../../components/Paywall/PaywallCta';
import { shd } from '@shd/jslib/models';

import { HitScopeHeader, HitScopeFeedback } from './components/HitScopeHeader';
import HitScopeClipList from './components/HitScopeCliplist';
import HitScopeFilters from './components/HitScopeFilters';
import HitScopeVideoPlayer from './components/HitScopeVideoPlayer';
import { HitScopeClip, Hit } from './hitScope.types';
import useHitScope from './useHitscope';
import HitScopeDownloadButton from './components/HitScopeDownloadButton';

const HitScope: React.FC<RouteComponentProps> = ({ history }) => {
  const { playerid: playerId } = useParams<{ playerid: string }>();
  const [currentClip, setCurrentClip] = useState<HitScopeClip>();
  const [manualSelectedClip, setManualSelectedClip] = useState<HitScopeClip>();
  const { playerInfo: playerPrimaryInfo, error: playerInfoError } =
    usePlayerPrimaryInfo(playerId);

  const [player, setPlayer] = useState<
    shd.ClaimedPlayer | shd.TeamPlayer | null
  >(null);

  const [endAtTs, setEndAtTs] = useState<number>(0);
  const [hitType, setHitType] = useState<Hit>(Hit.ALL_CONTACT);

  const userStore = useContext(UserContext);

  const { entitlementStatus: entitlementStatus, loading: entitlementLoading } =
    useEntitlements({ playerId, sport: 'bbsb' });

  const { fetchClips, clips, m3u8Text, selectedClip, loading, error } =
    useHitScope({
      type: 'player',
    });

  useZendeskWidget();

  const onClipChanged = (newClip: HitScopeClip) => {
    setCurrentClip(newClip);
  };

  useEffect(() => {
    setCurrentClip(selectedClip);
  }, [selectedClip]);

  useEffect(() => {
    fetchClips({
      playerId,
      endAtTs,
      good: hitType === Hit.HITS,
      qType: hitType,
    });
  }, [fetchClips, playerId, endAtTs, hitType]);

  useEffect(() => {
    if (playerPrimaryInfo) {
      setPlayer(playerPrimaryInfo.player);
    }
  }, [playerPrimaryInfo, playerInfoError]);

  useEffect(() => {
    analytics.track('HitScope View', {
      type: 'Player',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (playerId && playerId.toLowerCase().includes('tmsh01-')) {
    return <Redirect to={{ pathname: `/hitscope/teams/${playerId}` }} />;
  }

  if (!player) {
    return <BasicLoader size={200} fullPage />;
  }

  return (
    <Box gap="large" width="xlarge" margin="0 auto" pad="medium">
      <Card
        pad="medium"
        gap="medium"
        background="primary-8"
        onClick={() => {
          history.push(`/${playerId}`);
        }}
      >
        <Box
          direction="row"
          justify="between"
          onClick={() => {
            analytics.track('HitScope Player Profile Click', {
              playerId,
            });
          }}
        >
          <Text id="player-name-header" size="xlarge" weight="bold">
            {`${getPrivacyName(
              player.nameFirst,
              player.nameLast,
              player.embedNameFirstLevel,
              player.embedNameLastLevel
            )}`}
          </Text>
          <Button
            alignSelf="center"
            justify="end"
            plain
            reverse
            gap="xxsmall"
            label={<Text size="small">More videos</Text>}
            icon={<FormNext />}
            onClick={() => {
              history.push(`/${playerId}`);
            }}
          />
        </Box>
      </Card>
      <HitScopeHeader showHelp={true} />
      {entitlementLoading ? (
        <BasicLoader />
      ) : (
        <>
          {entitlementStatus ===
            ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT && (
            <PaywallLock isLoggedIn={!!userStore.authUser} isPrivate={false} />
          )}
          {entitlementStatus ===
            ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE && (
            <PaywallLock isLoggedIn={!!userStore.authUser} isPrivate={true} />
          )}
          {entitlementStatus ===
            ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN && (
            <PaywallCta
              mode="fullAccess"
              context={PaywallContext.PLAYER_HITSCOPE}
              playerId={playerId}
            />
          )}
          {entitlementStatus === ViewerEntitlementStatus.ENTITLED && (
            <>
              <HitScopeVideoPlayer
                clips={clips}
                m3u8Text={m3u8Text}
                onClipChanged={onClipChanged}
                seekToClip={manualSelectedClip}
              />
              <Box
                margin={{ top: '.5rem', bottom: '.5rem' }}
                direction="row"
                justify="between"
              >
                <Text size="small">
                  {`Watch every batted ball for ${getPrivacyName(
                    player.nameFirst,
                    player.nameLast,
                    player.embedNameFirstLevel,
                    player.embedNameLastLevel
                  )}. This video updates after every game.`}
                </Text>
                <HitScopeDownloadButton
                  numberOfClips={clips.length}
                  title={formatDownloadTitle(
                    `hitscope-${player?.nameFirst}-${player?.nameLast}`
                  )}
                  type={'player'}
                  downloadRequest={{
                    playerId,
                    endAtTs,
                    good: hitType === Hit.HITS,
                    qType: hitType,
                  }}
                />
              </Box>
              <HitScopeFeedback />
              <HitScopeFilters
                setEndAtTs={setEndAtTs}
                setHitType={setHitType}
                loading={loading}
                track={{ type: 'player', playerId }}
              />
              {clips && selectedClip && (
                <HitScopeClipList
                  track={{
                    type: 'player',
                    playerId,
                  }}
                  clips={clips}
                  setManualSelectedClip={setManualSelectedClip}
                  selectedClip={currentClip}
                  loading={loading}
                />
              )}
              {error && <Text color="red">{error}</Text>}
            </>
          )}
        </>
      )}
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default compose<any, RouteComponentProps>(
  withLayout,
  withRouter
)(HitScope);
